// Settings → Bailey: the receptionist's safety brakes, as the owner sees them
// (BE conciergeGuard, hardening 2026-10).
//
// The anomaly brake pauses the receptionist when a salon's hour is far over
// its own normal (new chats, replies, calls, texts) or its estimated spend
// spikes. It never resumes by itself: the owner (or a Beu admin) resumes it.
// While paused, clients get the booking link and the salon's number.

const REASONS = {
    cost_spike: 'Bailey’s running cost jumped well above normal in an hour.',
    session_spike: 'An unusual burst of new chats arrived in an hour.',
    reply_spike: 'An unusual number of chat messages arrived in an hour.',
    call_spike: 'An unusual number of phone calls arrived in an hour.',
    sms_spike: 'An unusual number of text messages were requested in an hour.'
};

/** Plain words for an alert's reason ('session_spike' → a sentence). */
export function alertReasonText(reason) {
    const r = String(reason || '');
    if (REASONS[r]) return REASONS[r];
    if (/_spike$/.test(r)) return 'Unusual activity, well above your normal, in an hour.';
    return 'Unusual activity.';
}

/** An alert's headline by kind. */
export function alertTitle(alert) {
    const kind = alert && alert.kind;
    if (kind === 'auto_pause') return 'Bailey paused itself';
    if (kind === 'cost_ceiling') return 'Daily limit reached';
    return 'Alert';
}

/**
 * The same rule the API applies to the put-through number (toll fraud: every
 * transfer is an outbound call on Beu's account): UK landline 01/02, 03, or a
 * UK mobile. Never 070/076, 08x, 09x, 05x or abroad. '' (empty) is fine — no
 * transfer, Bailey takes a message. A hint only; the API decides.
 */
export function normaliseUk(raw) {
    const s = String(raw || '').trim();
    if (!s) return '';
    let d = s.replace(/[^\d+]/g, '');
    if (d.startsWith('00')) d = `+${d.slice(2)}`;
    if (d.startsWith('+')) return /^\+44\d{9,10}$/.test(d) ? d : '';
    if (d.startsWith('44') && d.length >= 11) d = `+${d}`;
    else if (d.startsWith('0')) d = `+44${d.slice(1)}`;
    else if (/^7\d{9}$/.test(d)) d = `+44${d}`;
    return /^\+44\d{9,10}$/.test(d) ? d : '';
}

export function isSafeTransferNumber(raw) {
    if (!String(raw || '').trim()) return true;
    const n = normaliseUk(raw);
    if (!n) return false;
    if (/^\+44[12]\d{8,9}$/.test(n)) return true;
    if (/^\+443\d{9}$/.test(n)) return true;
    return /^\+447[1-57-9]\d{8}$/.test(n);
}

export const TRANSFER_RULE = 'Calls can only be put through to a UK landline (01, 02, 03) or UK mobile number.';

/**
 * A failed save, read for the put-through number: the API refuses with
 * reason 'transfer_number' (not an allowed number) or 'owner_only' (only the
 * owner may change where calls go). Its words, else ours; '' if it's not
 * about the number.
 */
export function transferErrorOf(payload) {
    const err = payload && payload.error;
    const reason = err && err.reason;
    if (reason === 'transfer_number') return (err && err.responseMessage) || TRANSFER_RULE;
    if (reason === 'owner_only') return (err && err.responseMessage) || 'Only the salon owner can change where calls are put through to.';
    return '';
}

/**
 * Resuming is the owner's alone. The API's refusal for a non-owner is worded
 * for the phone number (shared check), so say it for resuming instead.
 */
export function resumeErrorText(message) {
    const m = String(message || '');
    if (/owner/i.test(m)) return 'Only the salon owner can resume Bailey.';
    return 'Couldn’t resume Bailey just now. Try again.';
}
